// Survey data read from the backend (or hand-edited/legacy-migrated rows) can
// carry the right field under the wrong casing, e.g. "Question number" instead
// of Question_number, or Type instead of type. The app should tolerate any
// casing on read, but must always persist using the exact casing listed below
// so every other file in the SurveyBuilder tree - which all use these exact
// names - keeps working unmodified.

export const QUESTION_LEVEL_KEYS = [
  "Question_number", "type", "Qtitle", "Question_text", "logics", "rows",
  "columns", "statements", "fields", "oeparameters", "notes", "termLogic",
  "piping", "remRow", "instructions", "customCSS", "customJs",
  "alphabeticalOrder", "columnIndex", "_order", "_deleted", "isGrid", "grid",
  "assignedQuota",
  "rowsRandomisation", "rowsRotation", "rowFlipped", "rowAlphaOrder",
  "columnsRandomisation", "columnsRotation", "columnsFlipped", "columnsAlphaOrder",
  "rowsGroupRandomisation", "rowsGroupRotation", "rowGroupFlipped", "rowGroupAlphaOrder",
  "columnsGroupRandomisation", "columnsGroupRotation", "columnGroupFlipped", "columnGroupAlphaOrder",
];

// NOTE: "Pretext"/"Posttext" (CustomRows-only) and "preText"/"postText"
// (StringGrid/NumericGrid + oeparameters) are deliberately kept OUT of this
// list's case-insensitive folding target set below - see foldKeysToCanonical.
// Lowercased they're identical strings, so folding them together would
// silently merge two different fields.
export const ROW_LEVEL_KEYS = [
  "text", "index", "id", "exclusive", "fix", "other", "otherPiping",
  "otherRequired", "otherInputType", "otherSize", "screenout", "filterLogic",
  "header", "indexes", "col", "colIndex", "cells", "label", "logic",
  "tempIndex", "insertIndex", "termLogic", "punchLogic", "quotaCounts",
  "color", "imageURL", "preText", "postText", "min", "max", "type", "colType",
  "inputType", "prefill", "isDecimal", "decimalPlaces", "code", "title",
  "randomize", "hidden", "anchor", "rotationGroup", "settings", "name",
];

export const OEPARAM_KEYS = [
  "isHidden", "isRequired", "min", "max", "exact", "optional", "nboxes",
  "nboxesrequired", "isTextarea", "isDate", "isDrag", "isSlider",
  "isDropDown", "isCol", "isRightLeft", "isCarousel", "isNumberRank",
  "isMaxDiff", "maxDiffDesign", "isSumTotal", "isSum100", "isSequential",
  "isFirstComp", "isDuplicate", "isNoanswer", "isPageBreak", "istablelook",
  "tbCorner", "validationLogic", "preText", "postText", "prefill",
  "inputType", "term", "errorMessage", "disabledCells", "isSinCol",
  "isWithoutMaxDiff", "mainFolder", "maxdigit",
  "isAnswerButton", "responseMode", "sliderPosition", "isVerticalSlider",
  "isHorizontalRatingScale", "noOfColumns", "noOfRows", "isGridbars",
  "isCarouselGridbars", "isCardSort", "isAccordion", "isAccordionAnswerButton",
  "isDecimal", "decimalPlaces", "isDragDrop", "isDragDropRank", "isClick",
  "isLoop", "isUnbound", "isQuota", "quotaConfig", "isSamePage", "isReadOnly",
  "isNoTranslation", "noAnswerLabel", "isDateRange", "min_date", "max_date",
  "sum100Target", "summin", "summax", "size", "parameters", "past",
  "present", "future",
];

// Keys that must never be folded into each other even though they collide
// case-insensitively - each pair is a distinct field, not a casing variant.
const NO_FOLD_KEYS = new Set(["Pretext", "Posttext", "preText", "postText"]);

const isEmpty = (value) =>
  value === undefined || value === null || value === "";

/**
 * Renames any own key on `obj` that matches a `canonicalKeys` entry
 * case-insensitively (but isn't already spelled exactly right) onto its
 * canonical spelling. Never overwrites a canonical key that already holds a
 * non-empty value - the mis-cased duplicate is dropped instead. Keys in
 * NO_FOLD_KEYS, and keys that don't match any canonical entry, pass through
 * untouched.
 */
export const foldKeysToCanonical = (obj, canonicalKeys) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;

  const lowerToCanonical = new Map();
  canonicalKeys.forEach((k) => {
    if (!NO_FOLD_KEYS.has(k)) lowerToCanonical.set(k.toLowerCase(), k);
  });

  const result = { ...obj };
  Object.keys(obj).forEach((key) => {
    if (NO_FOLD_KEYS.has(key) || canonicalKeys.includes(key)) return;
    const canonical = lowerToCanonical.get(key.toLowerCase());
    if (!canonical) return;

    if (isEmpty(result[canonical])) {
      result[canonical] = result[key];
    }
    delete result[key];
  });

  return result;
};

const foldArrayItems = (arr, canonicalKeys) =>
  Array.isArray(arr) ? arr.map((item) => foldKeysToCanonical(item, canonicalKeys)) : arr;

export const normalizeQuestionObject = (question) => {
  if (!question || typeof question !== "object") return question;

  const folded = foldKeysToCanonical(question, QUESTION_LEVEL_KEYS);

  return {
    ...folded,
    ...(folded.rows ? { rows: foldArrayItems(folded.rows, ROW_LEVEL_KEYS) } : {}),
    ...(folded.columns ? { columns: foldArrayItems(folded.columns, ROW_LEVEL_KEYS) } : {}),
    ...(folded.statements ? { statements: foldArrayItems(folded.statements, ROW_LEVEL_KEYS) } : {}),
    ...(folded.fields ? { fields: foldArrayItems(folded.fields, ROW_LEVEL_KEYS) } : {}),
    ...(folded.oeparameters
      ? { oeparameters: foldKeysToCanonical(folded.oeparameters, OEPARAM_KEYS) }
      : {}),
  };
};

export const normalizeSurveyData = (sdataGroup) => {
  const normalized = {};
  Object.entries(sdataGroup || {}).forEach(([key, question]) => {
    normalized[key] = normalizeQuestionObject(question);
  });
  return normalized;
};
